// Shared between sw.ts (writes the payload) and routes/share-target.tsx
// (reads it back) so the Cache Storage keys can't drift out of sync.
export const SHARE_CACHE = "share-target-v1"
export const SHARE_META_KEY = "/share-target-meta"
export const SHARE_FILE_KEY = "/share-target-file"

export interface ShareTargetMeta {
  title: string
  text: string
  url: string
  fileName: string | null
  fileType: string | null
  /** Epoch ms when the service worker staged the share. Drives the TTL purge. */
  createdAt: number
}

/**
 * A staged share (possibly a bank statement PDF, in plaintext) must never sit
 * in Cache Storage indefinitely. Anything older than this is purged on SW
 * activate/startup and on app load, whatever happened to the page that
 * should have consumed it (crash, reload, closed tab).
 */
export const SHARE_TTL_MS = 10 * 60 * 1000

/** The slice of a Cache Storage `Cache` the share hand-off needs. */
export interface ShareCacheLike {
  put(key: string, res: Response): Promise<void>
  delete(key: string): Promise<boolean>
  match?(key: string): Promise<Response | undefined>
}

/** The slice of `CacheStorage` (window.caches / self.caches) the purge needs. */
export interface ShareCacheStorageLike {
  open(name: string): Promise<ShareCacheLike>
}

function defaultCacheStorage(): ShareCacheStorageLike | null {
  const g = globalThis as unknown as { caches?: ShareCacheStorageLike }
  return g.caches ?? null
}

/** Delete the staged share outright (consumed, dismissed, lock, sign-out). */
export async function purgeSharePayload(
  storage: ShareCacheStorageLike | null = defaultCacheStorage(),
): Promise<void> {
  if (!storage) return
  try {
    const cache = await storage.open(SHARE_CACHE)
    await cache.delete(SHARE_META_KEY)
    await cache.delete(SHARE_FILE_KEY)
  } catch {
    // Cache Storage unavailable (private window, quota) — nothing to purge.
  }
}

/** True when a staged share is past its TTL, or unreadable/unstamped (legacy). */
export function isShareExpired(meta: Partial<ShareTargetMeta> | null, now = Date.now()): boolean {
  if (!meta || typeof meta.createdAt !== "number") return true
  return now - meta.createdAt > SHARE_TTL_MS || meta.createdAt > now + 60_000
}

/**
 * Purge the staged share if it's expired, unstamped, or a file with no
 * metadata. Returns true when something was purged. Runs on SW
 * activate/startup and on app load (see cleanupSharePayloadOnStartup).
 */
export async function purgeExpiredSharePayload(
  storage: ShareCacheStorageLike | null = defaultCacheStorage(),
  now = Date.now(),
): Promise<boolean> {
  if (!storage) return false
  try {
    const cache = await storage.open(SHARE_CACHE)
    const metaRes = await cache.match?.(SHARE_META_KEY)
    const fileRes = await cache.match?.(SHARE_FILE_KEY)
    if (!metaRes && !fileRes) return false
    let meta: Partial<ShareTargetMeta> | null = null
    try {
      meta = metaRes ? ((await metaRes.json()) as Partial<ShareTargetMeta>) : null
    } catch {
      meta = null
    }
    if (!isShareExpired(meta, now)) return false
    await cache.delete(SHARE_META_KEY)
    await cache.delete(SHARE_FILE_KEY)
    return true
  } catch {
    return false
  }
}

/** Fire-and-forget startup hook for main.tsx and sw.ts. */
export function cleanupSharePayloadOnStartup(
  storage: ShareCacheStorageLike | null = defaultCacheStorage(),
  now = Date.now(),
): Promise<boolean> {
  return purgeExpiredSharePayload(storage, now)
}

/**
 * Stage an OS share-sheet POST (multipart form) for /share-target: metadata as
 * JSON under SHARE_META_KEY, the file's bytes under SHARE_FILE_KEY. The service
 * worker's route handler is a thin wrapper around this (sw.ts), so the logic
 * is testable without a real service worker. A malformed payload clears both
 * keys, so the page renders its "nothing shared" state instead of throwing.
 */
export async function stageSharePayload(
  readForm: () => Promise<FormData>,
  cache: ShareCacheLike,
  now = Date.now(),
): Promise<void> {
  try {
    const formData = await readForm()
    const text = (name: string) => {
      const v = formData.get(name)
      return typeof v === "string" ? v : ""
    }
    const rawFile = formData.get("file")
    const file = rawFile && typeof rawFile !== "string" ? (rawFile as File) : null

    const meta: ShareTargetMeta = {
      title: text("title"),
      text: text("text"),
      url: text("url"),
      fileName: file?.name ?? null,
      fileType: file?.type || null,
      createdAt: now,
    }
    await cache.put(
      SHARE_META_KEY,
      new Response(JSON.stringify(meta), { headers: { "Content-Type": "application/json" } }),
    )
    if (file) {
      await cache.put(
        SHARE_FILE_KEY,
        new Response(file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        }),
      )
    } else {
      // A share without a file must not pick up a previous share's file.
      await cache.delete(SHARE_FILE_KEY)
    }
  } catch {
    await cache.delete(SHARE_META_KEY)
    await cache.delete(SHARE_FILE_KEY)
  }
}
